import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { Checkbox } from '../../../../../components/forms/checkbox/checkbox.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Archive, Plus, RefreshCcw, X } from '../../../../../components/icon/solar-icons.js'
import {
    archiveSettingsCompensationType,
    createSettingsCompensationType,
    fetchSettingsCompensationTypes,
    restoreSettingsCompensationType,
    type SettingsCompensationType,
    updateSettingsCompensationType,
} from '../../../utils/portal-api.js'

export interface CompensationTypesSectionProps {
    lang: Language
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

interface CompensationTypeForm {
    name: string
}

const emptyForm: CompensationTypeForm = { name: '' }

export const CompensationTypesSection = ({ lang, showToast }: CompensationTypesSectionProps) => {
    const s = t.portal.settings.compensationTypes
    const [types, setTypes] = useState<SettingsCompensationType[]>([])
    const [showArchived, setShowArchived] = useState(false)
    const [slideOpen, setSlideOpen] = useState(false)
    const [editing, setEditing] = useState<SettingsCompensationType | null>(null)
    const [form, setForm] = useState<CompensationTypeForm>(emptyForm)
    const [saving, setSaving] = useState(false)

    const reload = async () => {
        const r = await fetchSettingsCompensationTypes(showArchived)
        if (r.ok) setTypes(r.data)
    }

    useEffect(() => {
        void reload()
    }, [showArchived])

    const openCreate = () => {
        setEditing(null)
        setForm(emptyForm)
        setSlideOpen(true)
    }

    const openEdit = (ct: SettingsCompensationType) => {
        setEditing(ct)
        setForm({ name: ct.name })
        setSlideOpen(true)
    }

    const close = () => {
        setSlideOpen(false)
        setEditing(null)
    }

    const save = async () => {
        if (!form.name.trim()) return
        setSaving(true)
        try {
            if (editing) {
                const r = await updateSettingsCompensationType(editing.id, {
                    name: form.name,
                })
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                showToast(s.updated(lang), 'success')
            } else {
                const r = await createSettingsCompensationType({
                    name: form.name,
                })
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                showToast(s.created(lang), 'success')
            }
            close()
            await reload()
        } finally {
            setSaving(false)
        }
    }

    const handleArchive = async (ct: SettingsCompensationType) => {
        const r = await archiveSettingsCompensationType(ct.id)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        showToast(s.archived_msg(lang), 'success')
        await reload()
    }

    const handleRestore = async (ct: SettingsCompensationType) => {
        const r = await restoreSettingsCompensationType(ct.id)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        showToast(s.restored(lang), 'success')
        await reload()
    }

    return (
        <div>
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">{s.title(lang)}</h3>
                <div className="portal-settings__header-controls">
                    <Checkbox
                        size="small"
                        label={s.showArchived(lang)}
                        isChecked={showArchived}
                        onChange={(isChecked) => setShowArchived(isChecked)}
                    />
                    <Button
                        variant="primary"
                        size="small"
                        iconLeft={Plus}
                        text={s.createType(lang)}
                        onClick={openCreate}
                    />
                </div>
            </div>

            {types.length === 0 ? (
                <div className="portal-settings__section-empty">{s.noTypes(lang)}</div>
            ) : (
                <div className="portal-settings__data-list">
                    {types.map((ct) => (
                        <div
                            key={ct.id}
                            className="portal-settings__data-row"
                            style={ct.is_archived ? { opacity: 0.55 } : undefined}
                        >
                            <div className="portal-settings__data-info">
                                <div className="portal-settings__data-name">
                                    {ct.name}
                                    {ct.is_archived ? (
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                fontSize: '0.75rem',
                                                color: 'var(--portal-text-secondary)',
                                            }}
                                        >
                                            ({s.archived(lang)})
                                        </span>
                                    ) : null}
                                    {ct.humaans_id ? (
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                fontSize: '0.75rem',
                                                color: 'var(--portal-text-secondary)',
                                            }}
                                            title={s.imported(lang)}
                                        >
                                            ⤓
                                        </span>
                                    ) : null}
                                </div>
                                <div className="portal-settings__data-meta">
                                    {s.usage(lang)}: {ct.usage_count}
                                </div>
                            </div>
                            <div className="portal-settings__data-actions">
                                {ct.is_archived ? (
                                    <button type="button" onClick={() => void handleRestore(ct)}>
                                        <RefreshCcw
                                            size={12}
                                            style={{ verticalAlign: 'middle', marginRight: 4 }}
                                        />
                                        {s.restore(lang)}
                                    </button>
                                ) : (
                                    <>
                                        <button type="button" onClick={() => openEdit(ct)}>
                                            {s.edit(lang)}
                                        </button>
                                        <button
                                            type="button"
                                            className="is-danger"
                                            onClick={() => void handleArchive(ct)}
                                        >
                                            <Archive
                                                size={12}
                                                style={{ verticalAlign: 'middle', marginRight: 4 }}
                                            />
                                            {s.archive(lang)}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={close} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {editing ? s.editType(lang) : s.createType(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.name(lang)}
                                    value={form.name}
                                    onChange={(value) => setForm((f) => ({ ...f, name: value }))}
                                />
                            </div>
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={saving ? s.saving(lang) : s.save(lang)}
                                isDisabled={saving || !form.name.trim()}
                                onClick={() => void save()}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
